import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Rocket, Compass, Trophy, Flag, Play, ArrowRight, Clock, Gauge, Projector, Laptop, MonitorSmartphone, Sparkles, Target, Scale, Eye, Users, ClipboardCheck } from 'lucide-react';
import PageShell, { SectionTitle } from './PageShell';
import { RVP_LIST } from '../data/rendezvousPedagogiques';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { Rocket, Compass, Trophy, Flag };

const COLOR_MAP = {
  sky:     { border: 'border-sky-500/40',     bg: 'bg-sky-950/25',     text: 'text-sky-300',     ring: 'hover:border-sky-400' },
  amber:   { border: 'border-amber-500/40',   bg: 'bg-amber-950/25',   text: 'text-amber-300',   ring: 'hover:border-amber-400' },
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-950/25', text: 'text-emerald-300', ring: 'hover:border-emerald-400' },
  red:     { border: 'border-red-500/40',     bg: 'bg-red-950/25',     text: 'text-red-300',     ring: 'hover:border-red-400' },
};

function renderCard(rvp, readCount) {
  const Icon = ICONS[rvp.icon] || Rocket;
  const c = COLOR_MAP[rvp.color] || COLOR_MAP.sky;
  return (
    <div key={rvp.slug} className={`rounded-2xl border ${c.border} ${c.bg} ${c.ring} p-5 sm:p-6 transition group relative overflow-hidden`} data-testid={`rvp-card-${rvp.slug}`}>
      <div className="absolute -top-4 -right-4 opacity-10 pointer-events-none">
        <Icon size={140} strokeWidth={1.2} />
      </div>
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${c.border} ${c.text} text-[10.5px] font-mono-spec uppercase tracking-wider font-bold`}>
            <Icon size={11} /> {rvp.code}
          </div>
          <div className="text-white/40 text-[10.5px] font-mono-spec inline-flex items-center gap-1"><Clock size={10} /> {rvp.duration}</div>
        </div>
        <h3 className="mt-4 font-serif-editorial text-white text-[24px] sm:text-[26px] leading-tight">{rvp.title}</h3>
        <p className="mt-1.5 text-white/60 text-[13.5px] leading-snug">{rvp.subtitle}</p>

        <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-start gap-2">
          <Gauge size={13} className="text-white/50 mt-0.5 shrink-0" />
          <div className="text-[12.5px] text-white/70"><span className="text-white/50">Timing : </span>{rvp.kilometrage}</div>
        </div>

        <p className="mt-4 text-white/70 text-[13.5px] leading-relaxed">{rvp.description}</p>

        {readCount > 0 && (
          <div
            className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-950/30 text-emerald-300 text-[11px] font-mono-spec"
            data-testid={`rvp-read-count-${rvp.slug}`}
            title="Nombre de familles ayant marqué cette séance comme lue à la maison"
          >
            <Users size={11} /> {readCount} {readCount > 1 ? 'familles ont révisé' : 'famille a révisé'}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2">
          {rvp.extraLink && (
            <Link
              to={rvp.extraLink.to}
              data-testid={`rvp-extra-${rvp.slug}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13px] font-bold transition w-full justify-center shadow-[0_0_24px_-6px_rgba(16,185,129,0.6)]"
            >
              <ClipboardCheck size={14} /> {rvp.extraLink.label} <ArrowRight size={12} />
            </Link>
          )}
          <Link
            to={`/enseignant/rendez-vous-pedagogiques/${rvp.slug}?mode=pro`}
            data-testid={`rvp-pro-${rvp.slug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-bold transition w-full justify-center shadow-[0_0_24px_-6px_rgba(239,68,68,0.6)]"
          >
            <Projector size={14} /> Mode vidéoprojection PRO <ArrowRight size={12} />
          </Link>
          <Link
            to={`/enseignant/rendez-vous-pedagogiques/${rvp.slug}`}
            data-testid={`rvp-open-${rvp.slug}`}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/30 text-[12.5px] font-semibold transition w-full justify-center"
          >
            <Play size={12} /> Aperçu simple (sans vidéoprojecteur)
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function RvpLandingPage() {
  const [readStats, setReadStats] = useState({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/seance-reviews/stats`);
        if (mounted && data && typeof data === 'object') setReadStats(data);
      } catch { /* silencieux — feature bonus, ne doit pas bloquer la page */ }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-10 sm:py-14">
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Enseignant · Rendez-vous pédagogiques (AAC + AM)</span>
        </nav>

        <SectionTitle
          kicker="Formations AAC & AM · Cahier des charges REMC"
          title="Rendez-vous pédagogiques"
        />
        <p className="mt-4 text-white/70 text-[15.5px] max-w-3xl leading-relaxed">
          Supports vidéoprojecteur pour animer les rendez-vous obligatoires : 3 RVP AAC (conduite accompagnée) + Séquence 7 AM (cyclomoteur & voiturette).
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3 text-[11.5px] text-white/50 font-mono-spec">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Parents obligatoirement présents</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Support ECSR officiel</span>
          <Link to="/formation/conduite-accompagnee-aac" data-testid="link-aac-doc" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/40 bg-red-500/10 text-red-200 hover:bg-red-500/20 transition">
            <Scale size={11} /> Cadre légal AAC · Art. R211-5 & Arrêté 29/07/2013
          </Link>
        </div>

        {/* Bandeau "Tester la config projecteur" — bien visible avant l'arrivée des élèves */}
        <div className="mt-8 rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-950/25 via-[#0d0d0e] to-[#0a0a0b] p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 relative overflow-hidden" data-testid="rvp-mire-banner">
          <div className="absolute -top-6 -right-6 opacity-[0.08] pointer-events-none">
            <Target size={140} strokeWidth={1} />
          </div>
          <div className="relative flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-fuchsia-500/50 bg-fuchsia-500/10 text-fuchsia-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <Target size={11} /> Avant la séance
            </div>
            <h3 className="mt-2.5 font-serif-editorial text-white text-[20px] sm:text-[22px] leading-tight">Vérifie ta config vidéoprojecteur en 30 secondes</h3>
            <p className="mt-1 text-white/60 text-[13px] leading-snug max-w-2xl">
              5 min avant l&apos;arrivée des élèves &amp; parents, lance la mire de test : couleurs, résolution, ratio, lisibilité, bords non coupés — tout est vérifié d&apos;un coup d&apos;œil.
            </p>
          </div>
          <button
            onClick={() => {
              try {
                // Tenter d'ouvrir sur l'écran secondaire si détecté
                const openMire = async () => {
                  const url = '/enseignant/rendez-vous-pedagogiques/mire';
                  let features = 'width=1280,height=800,menubar=no,toolbar=no,location=no';
                  try {
                    if (typeof window.getScreenDetails === 'function') {
                      const details = await window.getScreenDetails();
                      const screens = details.screens || [];
                      if (screens.length >= 2) {
                        const secondary = screens.find((s) => s !== details.currentScreen) || screens[1];
                        const left = secondary.availLeft ?? secondary.left ?? 0;
                        const top = secondary.availTop ?? secondary.top ?? 0;
                        const w = secondary.availWidth ?? secondary.width ?? 1280;
                        const h = secondary.availHeight ?? secondary.height ?? 800;
                        features = `left=${left},top=${top},width=${w},height=${h},menubar=no,toolbar=no,location=no,fullscreen=yes`;
                      }
                    }
                  } catch { /* noop */ }
                  const win = window.open(url, 'rvp-mire-test', features);
                  if (!win) alert("Autorise les popups pour ouvrir la mire de test.");
                };
                openMire();
              } catch { /* noop */ }
            }}
            data-testid="rvp-landing-open-mire"
            className="relative shrink-0 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-[13.5px] font-bold transition shadow-lg shadow-fuchsia-900/40"
          >
            <Target size={14} /> Ouvrir la mire de test
          </button>
        </div>

        <div className="mt-10 space-y-10">
          {/* Groupe AAC */}
          <section>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-mono-spec font-bold">Formation AAC · Conduite Accompagnée</div>
                <h2 className="mt-1 font-serif-editorial text-white text-[22px] sm:text-[26px]">Du RDV préalable (AFFI) aux 3 000 km</h2>
              </div>
              <span className="text-[11px] font-mono-spec text-white/40">Arrêté 22/03/1993 modifié</span>
            </div>
            <div className="grid md:grid-cols-3 gap-5" data-testid="rvp-cards-aac">
              {RVP_LIST.filter((r) => r.formation === 'AAC').map((rvp) => renderCard(rvp, readStats[rvp.slug] || 0))}
            </div>
          </section>

          {/* Groupe AM */}
          <section>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-mono-spec font-bold">Formation AM · Cyclo 50cc & Voiturette</div>
                <h2 className="mt-1 font-serif-editorial text-white text-[22px] sm:text-[26px]">Séquence de synthèse avec les parents</h2>
              </div>
              <span className="text-[11px] font-mono-spec text-white/40">Arrêté 08/11/2012 modifié</span>
            </div>
            <div className="grid md:grid-cols-3 gap-5" data-testid="rvp-cards-am">
              {RVP_LIST.filter((r) => r.formation === 'AM').map((rvp) => renderCard(rvp, readStats[rvp.slug] || 0))}
            </div>
          </section>
        </div>

        <div className="mt-12 rounded-2xl border border-red-500/25 bg-gradient-to-br from-red-950/20 via-[#0d0d0e] to-[#0a0a0b] p-5 sm:p-8 relative overflow-hidden" data-testid="rvp-pro-guide">
          <div className="absolute -top-8 -right-8 opacity-[0.06] pointer-events-none">
            <Projector size={220} strokeWidth={1} />
          </div>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-red-500/50 bg-red-500/10 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
              <Sparkles size={11} /> Mode PRO Vidéoprojection · Comment ça marche
            </div>
            <h3 className="mt-3 font-serif-editorial text-white text-[26px] sm:text-[30px] leading-tight">Deux écrans, zéro stress</h3>
            <p className="mt-2 text-white/65 text-[14px] max-w-2xl leading-relaxed">
              Ton PC affiche tes notes, timings et le QR code de sondage. Le vidéoprojecteur affiche uniquement les slides pour la salle. Tout est synchronisé automatiquement.
            </p>

            <div className="mt-6 grid md:grid-cols-3 gap-4">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 relative">
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 font-mono-spec text-[12px] flex items-center justify-center font-bold">1</div>
                <Projector size={22} className="text-red-300" strokeWidth={1.5} />
                <div className="mt-3 font-serif-editorial text-white text-[17px]">Clique sur « Mode vidéoprojection PRO »</div>
                <p className="mt-1.5 text-white/60 text-[12.5px] leading-relaxed">Sur la carte du RVP, choisis le bouton rouge. Une popup t&apos;explique les 3 étapes.</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 relative">
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 font-mono-spec text-[12px] flex items-center justify-center font-bold">2</div>
                <MonitorSmartphone size={22} className="text-red-300" strokeWidth={1.5} />
                <div className="mt-3 font-serif-editorial text-white text-[17px]">Glisse la vitrine sur le vidéoprojecteur</div>
                <p className="mt-1.5 text-white/60 text-[12.5px] leading-relaxed">Une 2ᵉ fenêtre s&apos;ouvre. Fais-la glisser sur l&apos;écran projeté, puis passe-la en plein écran avec <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono-spec">F</kbd>.</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 relative">
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 font-mono-spec text-[12px] flex items-center justify-center font-bold">3</div>
                <Laptop size={22} className="text-red-300" strokeWidth={1.5} />
                <div className="mt-3 font-serif-editorial text-white text-[17px]">Anime depuis ton PC</div>
                <p className="mt-1.5 text-white/60 text-[12.5px] leading-relaxed">Tu vois tes notes formateur, la salle voit les slides. Flèches <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono-spec">←</kbd> <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-mono-spec">→</kbd> pour naviguer.</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2 text-[11.5px] text-white/50 font-mono-spec">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5"><Clock size={10} /> Synchronisation live</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">QR sondage intégré</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Chrono + notes formateur</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5">Support ECSR officiel</span>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
